import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react'

import { RichTextEditor } from '@/components/shared/rich-text-editor'
import {
  useRichTextEditor,
  type RichTextVariableToken,
} from '@/hooks/use-rich-text-editor'
import { cn } from '@/lib/utils'

import type { TemplateVariableTextEditorHandle } from './template-variable-text-editor'

interface TemplateVariableRichTextEditorProps {
  id?: string
  value: string
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder?: string
  variableTokens: RichTextVariableToken[]
  minHeight?: string
  className?: string
}

const htmlTagPattern = /<\/?[a-z][\s\S]*>/i
const emptyEditorHtmlPattern = /^<p>\s*(?:<br\s*\/?>)?\s*<\/p>$/i

function escapeHTML(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function plainTextToHTML(value: string) {
  return value
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHTML(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

function toEditorContent(value: string) {
  if (!value) return ''
  return htmlTagPattern.test(value) ? value : plainTextToHTML(value)
}

function normalizeEditorHTML(value: string) {
  return emptyEditorHtmlPattern.test(value.trim()) ? '' : value
}

export const TemplateVariableRichTextEditor = forwardRef<
  TemplateVariableTextEditorHandle,
  TemplateVariableRichTextEditorProps
>(function TemplateVariableRichTextEditor(
  {
    id,
    value,
    onChange,
    onFocus,
    onBlur,
    placeholder,
    variableTokens,
    minHeight = '160px',
    className,
  },
  ref
) {
  const editorContent = useMemo(() => toEditorContent(value), [value])
  const editor = useRichTextEditor({
    id,
    initialContent: editorContent,
    placeholder,
    minHeight,
    variableTokens,
    highlightUnresolvedTemplateVariables: true,
    onFocus,
    onBlur,
    onUpdate: (html) => {
      onChange(normalizeEditorHTML(html))
    },
  })

  useEffect(() => {
    if (!editor) return

    if (editor.getHTML() === editorContent) return
    editor.commands.setContent(editorContent)
  }, [editor, editorContent])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editor?.commands.focus()
      },
      insertText: (text: string) => {
        editor?.chain().focus().insertContent(text).run()
      },
    }),
    [editor]
  )

  return (
    <RichTextEditor
      editor={editor}
      minHeight={minHeight}
      className={cn('text-sm', className)}
    />
  )
})
